// 📂 src/components/dashboard/CameraLogViewer.jsx
import { useState, useEffect } from "react";
import authAxios from "../../../api/authAxios";

export default function CameraLogViewer({ cameraId, onClose }) {
  const [log, setLog] = useState("");
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState(50);
  const [error, setError] = useState(null);

  // --------------------------
  // Fetch Diagnostics + Logs
  // --------------------------
  useEffect(() => {
    if (!cameraId) return;

    const fetchDiag = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authAxios.get(`/cameras/${cameraId}/log-tail/`, {
          params: { lines },
        });

        if (res.data?.ok === false) {
          setError(res.data.error || "Backend error");
          setLog("");
          setDiag(null);
        } else {
          setLog(res.data.log || "");
          setDiag({
            status: res.data.status,
            connection_error: res.data.connection_error,
            last_error_at: res.data.last_error_at,
            root_cause: res.data.root_cause,
            suggested_fix: res.data.suggested_fix,
            last_recording: res.data.last_recording,
            recording_gap_minutes: res.data.recording_gap_minutes,
            coverage_24h_percent: res.data.coverage_24h_percent,
          });
        }
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          err.response?.statusText ||
          err.message ||
          "Unknown error";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchDiag();
  }, [cameraId, lines]);

  // --------------------------
  // Restart Stream Handler
  // --------------------------
  const handleRestart = async () => {
    try {
      await authAxios.post(`/cameras/${cameraId}/restart_stream/`);
      // re-fetch diagnostics
      const res = await authAxios.get(`/cameras/${cameraId}/log-tail/`, {
        params: { lines },
      });

      setLog(res.data.log || "");
      setDiag(res.data);
    } catch (err) {
      console.error("Restart failed:", err);
      alert("Failed to restart camera stream.");
    }
  };

  // --------------------------
  // Draggable Window Logic
  // --------------------------
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    setIsDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const onMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - startPos.x,
      y: e.clientY - startPos.y,
    });
  };
  const onMouseUp = () => setIsDragging(false);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  });

  // --------------------------
  // Render UI
  // --------------------------
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"
      onMouseUp={onMouseUp}
    >
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          resize: "both",
        }}
        className="relative w-full max-w-3xl h-[600px] rounded shadow-lg overflow-hidden border border-gray-700 bg-neutral-900 text-gray-200"
      >
        {/* Header (drag handle) */}
        <div
          onMouseDown={onMouseDown}
          className="cursor-move bg-neutral-800 px-4 py-2 flex justify-between items-center select-none"
        >
          <span className="font-semibold text-sm">Diagnostics — Camera #{cameraId}</span>

          <div className="flex items-center gap-3">
            <select
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
              className="text-xs bg-neutral-800 border border-gray-600 rounded px-1 py-0.5"
            >
              {[50, 100, 200, 500, 1000].map((n) => (
                <option key={n} value={n}>
                  {n} lines
                </option>
              ))}
            </select>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* ---------------------- */}
        {/* Diagnostics Section    */}
        {/* ---------------------- */}
        {diag && (
          <div className="px-4 py-3 bg-neutral-800 border-b border-neutral-700 text-sm space-y-4">

            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="font-semibold">Status:</span>

              <span
                className={`px-2 py-1 rounded text-xs ${
                  diag.status === "online"
                    ? "bg-green-600/20 text-green-400"
                    : diag.status === "connecting"
                    ? "bg-yellow-600/20 text-yellow-400"
                    : diag.status === "inactive"
                    ? "bg-gray-600/20 text-gray-400"
                    : "bg-red-600/20 text-red-400"
                }`}
              >
                {diag.status}
              </span>
            </div>

            {/* Connection Error */}
            {diag.connection_error && (
              <div>
                <p className="font-semibold">Connection Error:</p>
                <p className="text-red-400 text-xs">{diag.connection_error}</p>
              </div>
            )}

            {/* Root Cause */}
            {diag.root_cause && (
              <div>
                <p className="font-semibold text-amber-400">Root Cause:</p>
                <p className="text-xs">{diag.root_cause}</p>
              </div>
            )}

            {/* Fix Suggestions */}
            {diag.suggested_fix?.length > 0 && (
              <div>
                <p className="font-semibold">Suggested Fix:</p>
                <ul className="list-disc pl-4 text-xs text-gray-400 space-y-1">
                  {diag.suggested_fix.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recording Health */}
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-gray-400">Last Recording</p>
                <p className="font-semibold">
                  {diag.last_recording
                    ? new Date(diag.last_recording).toLocaleString()
                    : "—"}
                </p>
              </div>

              <div>
                <p className="text-gray-400">Gap</p>
                <p className="font-semibold">
                  {diag.recording_gap_minutes != null
                    ? `${diag.recording_gap_minutes} min`
                    : "—"}
                </p>
              </div>

              <div>
                <p className="text-gray-400">24h Coverage</p>
                <p className="font-semibold">
                  {diag.coverage_24h_percent != null
                    ? `${diag.coverage_24h_percent}%`
                    : "—"}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => alert("Restart Stream coming soon")}
                className="px-3 py-1.5 bg-blue-600 rounded text-xs hover:bg-blue-700"
              >
                Restart Stream
              </button>

              <button
                onClick={() => alert("Open Camera UI coming soon")}
                className="px-3 py-1.5 bg-neutral-700 rounded text-xs hover:bg-neutral-600"
              >
                Open Camera UI
              </button>
            </div>
          </div>
        )}

        {/* ---------------------- */}
        {/* Raw Log Terminal        */}
        {/* ---------------------- */}
        <div className="p-3 overflow-y-auto h-full font-mono text-xs bg-black text-green-400 whitespace-pre-wrap border-t border-gray-800">
          {loading ? (
            <span className="text-gray-400">Loading logs...</span>
          ) : error ? (
            <span className="text-red-400">⚠️ Error: {error}</span>
          ) : log ? (
            log
          ) : (
            <span className="text-gray-500">(no log entries)</span>
          )}
        </div>
      </div>
    </div>
  );
}

